import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { OutreachCampaignLive } from '../outreachTypes';
import { PlugLine } from './upNext/PlugLine';
import { dayCount, plannedTitle } from './upNext/upNextCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Up next; each part uses its own slice of it */
import upNextStyles from './upNext/UpNext.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { dayLabel } from './campaignFormat';
import { nextLine, todayTitle } from './todayLine';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TodayCardProps {
  live: OutreachCampaignLive;
  /** The pitches a day of this campaign (`campaigns.get` `dailyRate`); null while it loads. */
  rate: number | null;
  /** The people that the find added last night (`poolSummary.newTotal`); null while it loads. */
  newFits: number | null;
  /** True on the day of the start: `newFits` is every fit so far, not last night's (QA B06). */
  soFar: boolean;
  now: Date;
}

/**
 * Today (design outreach-v2 g1, g4, s2-s4): the ring "3/5", "3 pitched, 2 go out by 11:40" or
 * "0 out yet · 3 go out tomorrow", the next send, the people that Plug found last night and the
 * "Plug is working" line. A turning arc on the ring while Plug writes or sends. Below Label the
 * offer "Pitch 15 a day on Label" opens the plan offer with Label picked (`outreachRate`).
 */
const TodayCardComponent: React.FC<TodayCardProps> = ({ live, rate, newFits, soFar, now }) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const openUpgradePrompt = useUpgradePrompt();
  const sent = live.today?.sent ?? 0;
  const { planned } = live;
  const total = Math.max(dayCount(rate, live.warmup) ?? live.today?.cap ?? 0, sent);
  const working = (planned?.pitches ?? []).some((pitch) => pitch.state === 'writing' || pitch.state === 'sending');
  const plannedCopy =
    live.phase === 'sending' && planned !== null ? plannedTitle(intl, planned.pitches, planned.total, sent, now) : null;
  const percent = total === 0 ? 0 : Math.min(100, Math.round((sent / total) * 100));
  const next = live.phase !== 'sending' ? null : plannedCopy !== null ? plannedCopy.sub : nextLine(intl, live.today);
  const fits =
    newFits !== null && newFits > 0
      ? intl.formatMessage(soFar ? messages.newFitsSoFar : messages.newFits, { count: newFits, song: live.song.title })
      : null;
  const sub = [next, fits].filter((part): part is string => part !== null).join(' ');

  return (
    <section className={styles.card} data-testid="today-card">
      <div className={styles.today}>
        <div
          className={styles.ring}
          style={{ '--p': percent } as React.CSSProperties}
          role="img"
          aria-label={intl.formatMessage(messages.ringLabel, { sent, rate: total })}
          data-testid="today-ring"
        >
          {working && <i className={upNextStyles.arc} aria-hidden />}
          <span className={styles.ringValue} aria-hidden>
            {sent}
            <small>/{total}</small>
          </span>
        </div>
        <div className={styles.todayText}>
          <span className={shared.eyebrow}>
            {intl.formatMessage(messages.todayDate, { date: dayLabel(intl, new Date().toISOString()) })}
          </span>
          <h2 className={styles.todayTitle}>{plannedCopy?.title ?? todayTitle(intl, live.phase, live.today)}</h2>
          {sub !== '' && <p className={`${shared.sub} m-0`}>{sub}</p>}
          {live.phase === 'sending' && planned !== null && planned.total > 0 && planned.nextBatchAt !== null && (
            <PlugLine nextBatchAt={planned.nextBatchAt} now={now} rate={rate} warmup={live.warmup} />
          )}
        </div>
        {!plan.canUseAutopilot && !plan.isDemo && (
          <div className={styles.offer}>
            <SMLButton
              size="sm"
              color="primary"
              omitCap
              onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreachRate, live.song.title)}
              testID="today-label-offer"
            >
              {intl.formatMessage(messages.labelOffer)}
            </SMLButton>
            <span className={shared.small}>{intl.formatMessage(messages.labelOfferSub)}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export const TodayCard = TypedMemo(TodayCardComponent);
